import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, SHIPPING_AUTOMATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

import * as S from './FeasibilityRuleForm.styled';

/**
 * @author @omerrkosar
 */

const OtomationTestForm = NiceModal.create(() => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					orderId: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const { showError } = useAlert();
	const [testResult, setTestResult] = useState();
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			orderId: null,
			reverseShipment: false
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const isLoading = isFormLoading || isSubmitting;

	const onSubmit = async (formData) => {
		try {
			const response = await axiosPrivate.post(SHIPPING_AUTOMATION.GET_AUTOMATION_TEST_RESULT, {
				ID: formData.orderId,
				reverseShipment: formData.reverseShipment
			});
			const { data, status } = response;
			if (status === 200) {
				setTestResult(data);
			}
		} catch (error) {
			AppLogger.error(error);
			if (error.response.data.code === 'error.CFR04') {
				showError({ message: 'error.CFR04' });
			} else showError();
		}
	};

	useEffect(() => {
		if (watch('orderId') === null || (watch('orderId') !== null && !isLoading)) {
			setTestResult(null);
		}
	}, [watch('orderId')]);

	const orderIdAutocompleteColumns = useMemo(() => [{ accessor: 'orderID', width: 200 }], []);

	return (
		<Modal fullwidth maxWidth="medium">
			<ModalTitle>
				<Typography variant="p1">otomationTest</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="orderNo" isLoading={isLoading} required>
						<AutoComplete
							name="orderId"
							autoCompleteName={AUTOCOMPLETE.ORDERS}
							columns={orderIdAutocompleteColumns}
							selectionLabelAccessor={['orderID']}
							value={watch('orderId')}
							setValue={(value) => {
								setValue('orderId', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
								setTestResult(null);
							}}
						/>
					</FormItem>
					<FormItem label="reverseShipment" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="reverseShipment" />
					</FormItem>

					{testResult && (
						<S.RowContainer className="outer">
							<FormItem
								colSpan={{
									md: 4
								}}
								label="feasibilityRulesThatHasBeenMatchedWithThisOrder">
								{testResult?.feasibility?.length > 0 ? (
									testResult?.feasibility?.map((rule) => (
										<S.RowContainer key={rule}>
											<Icon icon={faCircleCheck} color="green" />
											<Typography noWrap>{rule}</Typography>
										</S.RowContainer>
									))
								) : (
									<Typography noWrap>noMatchingRule</Typography>
								)}
							</FormItem>
							<FormItem
								colSpan={{
									md: 3
								}}
								label="selectedPriority">
								{testResult?.selectedPriority ? (
									<S.RowContainer>
										<Icon icon={faCircleCheck} color="green" />
										<Typography noWrap>{testResult.selectedPriority}</Typography>
									</S.RowContainer>
								) : (
									<Typography noWrap>noMatchingRule</Typography>
								)}
							</FormItem>
							<FormItem
								colSpan={{
									md: 5
								}}
								label="result">
								<S.ResultContainer>
									{testResult?.coveredDeliveryCompanies?.length > 0 ? (
										testResult?.coveredDeliveryCompanies.map((dc, index) => (
											<S.RowContainer key={index} className="dc">
												<Image url={dc?.logo} width="3.5rem" height="3.5rem" />
												<S.MessageContent>
													<Typography noWrap variant="p3">
														{dc.deliveryCompany}
													</Typography>
													<Typography noWrap variant="p3">
														{dc.subName}
													</Typography>
												</S.MessageContent>
											</S.RowContainer>
										))
									) : (
										<Typography noWrap>noMatchingDeliveryCompany</Typography>
									)}
								</S.ResultContainer>
							</FormItem>
						</S.RowContainer>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button variant="outline" label="cancel" onClick={() => modal.remove(OtomationTestForm)} />
				<Button
					loading={isLoading}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty || watch('orderId') === null}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label="test"
				/>
			</ModalActions>
		</Modal>
	);
});

export default OtomationTestForm;
